import React, { useState, useEffect, useRef } from 'react';

// =================================== Final With Images ====================================================================
export const FoodCards = ({ foodRecommendations }) => {
  const CARD_WIDTH = 350;
  const CARD_HEIGHT = 500;
  // 190 rather than 200 so the outermost card, once projected by the stage's
  // 1500px perspective, still lands inside this column instead of bleeding into
  // the weather panel on the left or the info panel on the right.
  const CARD_SPACING = 190;
  // Symmetric coverflow: the same number of cards recede on both sides with
  // identical transforms. The right side used to fan out across the screen in a
  // widening cascade, which is what left no room for the info panel and made
  // the alert panel collide with it.
  const VISIBLE_SIDE = 2;

  const [trackOffset, setTrackOffset] = useState(0);
  const [isShuffling, setIsShuffling] = useState(false);
  // The animation reads/writes position through refs so it never depends on a
  // stale render closure — the previous version restarted from an old
  // trackOffset and could leave orphaned setTimeout→rAF chains running,
  // which piled up over hours (each weather poll re-triggered the effect)
  // until the carousel stuttered and froze.
  const trackOffsetRef = useRef(0);
  const rafRef = useRef(null);
  const timeoutRef = useRef(null);
  const lastShownIndexRef = useRef(-1);
  const totalCardsRef = useRef(0);

  const totalCards = foodRecommendations?.length || 0;
  totalCardsRef.current = totalCards;

  // Restart the loop only when the actual dishes change, not when the 60 s
  // weather poll produces a fresh-but-identical array.
  const contentKey = (foodRecommendations || []).map((food) => food.name).join('|');

  useEffect(() => {
    if (totalCards <= 1) return undefined;

    let cancelled = false;

    const spin = () => {
      if (cancelled) return;
      const total = totalCardsRef.current;
      if (total <= 1) return;

      setIsShuffling(true);

      // Pick a random target different from the last one shown.
      let targetIndex;
      do {
        targetIndex = Math.floor(Math.random() * total);
      } while (targetIndex === lastShownIndexRef.current && total > 1);
      lastShownIndexRef.current = targetIndex;

      const currentPosition = trackOffsetRef.current;
      const currentIndex = ((Math.round(currentPosition) % total) + total) % total;

      let distance = (targetIndex - currentIndex + total) % total;
      if (distance === 0) distance = total;

      const extraRotations = 2 + Math.floor(Math.random() * 2);
      const totalDistance = distance + (extraRotations * total);
      const startTime = performance.now();
      const duration = 2800;

      const animate = (now) => {
        if (cancelled) return;
        const progress = Math.min((now - startTime) / duration, 1);
        const easeOut = 1 - Math.pow(1 - progress, 5);
        const newOffset = currentPosition + (totalDistance * easeOut);

        trackOffsetRef.current = newOffset;
        setTrackOffset(newOffset);

        if (progress < 1) {
          rafRef.current = requestAnimationFrame(animate);
        } else {
          const finalPosition = Math.round(currentPosition + totalDistance);
          trackOffsetRef.current = finalPosition;
          setTrackOffset(finalPosition);
          setIsShuffling(false);
          timeoutRef.current = setTimeout(spin, 1500);
        }
      };

      rafRef.current = requestAnimationFrame(animate);
    };

    timeoutRef.current = setTimeout(spin, 1000);

    // Cancel everything this effect ever scheduled — exactly one spin chain
    // exists at a time.
    return () => {
      cancelled = true;
      clearTimeout(timeoutRef.current);
      cancelAnimationFrame(rafRef.current);
      setIsShuffling(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contentKey]);

  if (!foodRecommendations || foodRecommendations.length === 0) {
    return (
      <div
        style={{
          width: "50%",
          height: "100vh",
          // background: "#0a0a0a",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <p style={{ color: "#666", fontSize: "20px" }}>
          No recommendations available
        </p>
      </div>
    );
  }

  const visibleCards = [];
  for (let offset = -VISIBLE_SIDE; offset <= VISIBLE_SIDE; offset++) {
    const baseIndex = Math.floor(trackOffset) + offset;
    const wrappedIndex = ((baseIndex % totalCards) + totalCards) % totalCards;
    visibleCards.push({ 
      index: wrappedIndex,
      position: baseIndex
    });
  }

  let centerCardIndex = Math.round(trackOffset) % totalCards;
  if (centerCardIndex < 0) centerCardIndex += totalCards;

  return (
    <div
      style={{
        width: "50%",
        height: "100vh",
        // background: "#0a0a0a",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        position: "relative",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          position: "relative",
          width: "100%",
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          perspective: "1500px",
          perspectiveOrigin: "center center",
          // This column now sits between the two 25% panels (25vw–75vw), so its
          // own centre already is the screen centre — no stage offset needed.
        }}
      >
        {visibleCards.map(({ index, position }) => {
          const cardOffset = position - trackOffset;
          const absOffset = Math.abs(cardOffset);
          const isCenter = absOffset < 0.5;

          // Mirrored either side of the highlighted card: same spacing, same
          // recession, same tilt and same dimming, so the queue on the right
          // reads exactly like the one on the left.
          const translateX = cardOffset * CARD_SPACING;
          const translateZ = isCenter ? 0 : -absOffset * 150;
          const translateY = isCenter ? -6 : 0;
          const rotateY = cardOffset * -22;
          const rotateX = isCenter ? 0 : 8;
          const brightness = isCenter ? 1 : Math.max(0.35, 1 - absOffset * 0.22);
          // Blur re-rasterizes the card on every changed frame — far too
          // expensive to animate on SMD player hardware. Only apply it once
          // the carousel has settled.
          const blur = isCenter || isShuffling ? 0 : Math.min(2, absOffset * 0.5);
          const scale = isCenter && !isShuffling ? 1.005 : 1;

          const food = foodRecommendations[index];

          return (
            <div
              key={`${index}-${position}`}
              style={{
                position: "absolute",
                width: `${CARD_WIDTH}px`,
                height: `${CARD_HEIGHT}px`,
                transformStyle: "preserve-3d",
                transform: `translate3d(${translateX}px, ${translateY}px, ${translateZ}px) rotateY(${rotateY}deg) rotateX(${rotateX}deg) scale(${scale})`,
                opacity: 1,
                filter: `brightness(${brightness})${blur ? ` blur(${blur}px)` : ''}`,
                zIndex: isCenter ? 100 : Math.round(50 - absOffset * 10),
                transition: isShuffling ? "none" : "transform 0.3s ease-out, filter 0.3s ease-out",
                // Keep each card on its own compositor layer so per-frame
                // transform updates don't repaint neighbours.
                willChange: "transform, filter",
                backfaceVisibility: "hidden",
              }}
            >
              <div
                style={{
                  width: "100%",
                  height: "100%",
                  // url() is quoted on purpose: webpack keeps the original
                  // basename in the emitted filename, so an asset named with a
                  // space produced invalid CSS and the card rendered as an
                  // empty outline. Quoting makes any future filename safe.
                  background: `linear-gradient(135deg, rgba(0, 0, 0, ${isCenter ? 0 : 0.45}), rgba(0, 0, 0, ${isCenter ? 0 : 0.45})), url("${food.image}") center/cover no-repeat`,
                  border: "2px solid rgba(255, 255, 255, 0.35)",
                  boxShadow: isCenter
                    ? `0 0 18px rgba(255, 255, 255, 0.45), 0 0 40px rgba(255, 255, 255, 0.25), 0 35px 90px rgba(0,0,0,0.6)`
                    : `0 0 6px rgba(255, 255, 255, 0.18), 0 15px 40px rgba(0,0,0,0.45)`,
                  borderRadius: "20px",
                  boxSizing: "border-box",
                  transition: "box-shadow 0.6s ease, background 0.6s ease",
                }}
              />
            </div>
          );
        })}
      </div>

      <div
        style={{
          position: "absolute",
          bottom: "40px",
          // Centred in this column, which is now also the screen centre.
          left: "50%",
          transform: "translateX(-50%)",
          display: "flex",
          gap: "10px",
          zIndex: 200,
        }}
      >
        {foodRecommendations.map((_, idx) => {
          const normalizedCenter = ((Math.round(trackOffset) % totalCards) + totalCards) % totalCards;
          return (
            <div
              key={idx}
              style={{
                width: "10px",
                height: "10px",
                borderRadius: "50%",
                background: idx === normalizedCenter ? "#fff" : "#333",
                transition: "background 0.4s ease",
                boxShadow:
                  idx === normalizedCenter
                    ? "0 0 12px rgba(255,255,255,0.6)"
                    : "none",
              }}
            />
          );
        })}
      </div>
    </div>
  );
};

// // Alerts Panel Component
// export const AlertsPanel = ({ weatherData }) => {
//   const [alerts, setAlerts] = useState([]);

//   useEffect(() => {
//     const newAlerts = [];
    
//     // Temperature alerts
//     if (weatherData.temperature > 40) {
//       newAlerts.push({
//         type: 'danger',
//         message: 'Extreme heat warning',
//         icon: '🔥'
//       });
//     } else if (weatherData.heatIndex > 35) {
//       newAlerts.push({
//         type: 'warning',
//         message: 'High heat index alert',
//         icon: '⚠️'
//       });
//     }
    
//     if (weatherData.temperature < 5) {
//       newAlerts.push({
//         type: 'warning',
//         message: 'Cold weather warning',
//         icon: '❄️'
//       });
//     }
    
//     // Humidity alerts
//     if (weatherData.humidity > 85) {
//       newAlerts.push({
//         type: 'info',
//         message: 'Very high humidity levels',
//         icon: '💧'
//       });
//     }
    
//     // Wind alerts
//     if (weatherData.windSpeed > 40) {
//       newAlerts.push({
//         type: 'warning',
//         message: 'Strong wind advisory',
//         icon: '💨'
//       });
//     } else if (weatherData.windSpeed > 25) {
//       newAlerts.push({
//         type: 'info',
//         message: 'Moderate winds expected',
//         icon: '🍃'
//       });
//     }
    
//     // Rain alerts
//     if (weatherData.rainChance > 70) {
//       newAlerts.push({
//         type: 'info',
//         message: 'High chance of rain',
//         icon: '🌧️'
//       });
//     }
    
//     // UV alerts
//     if (weatherData.uvIndex > 8) {
//       newAlerts.push({
//         type: 'warning',
//         message: 'Very high UV index',
//         icon: '☀️'
//       });
//     }
    
//     // All clear
//     if (newAlerts.length === 0) {
//       newAlerts.push({
//         type: 'success',
//         message: 'Weather conditions normal',
//         icon: '✓'
//       });
//     }
    
//     setAlerts(newAlerts);
//   }, [weatherData]);

//   return (
//     <div style={{
//       width: '25%',
//       height: '100vh',
//       padding: '20px',
//       display: 'flex',
//       flexDirection: 'column'
//     }}>
//       <div style={{
//         height: '100%',
//         background: 'rgba(15, 15, 15, 0.6)',
//         backdropFilter: 'blur(10px)',
//         WebkitBackdropFilter: 'blur(10px)',
//         border: '1px solid rgba(255, 255, 255, 0.08)',
//         borderRadius: '24px',
//         padding: '40px 30px',
//         boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.37)',
//         display: 'flex',
//         flexDirection: 'column',
//         overflowY: 'auto'
//       }}>
//         <h2 style={{
//           color: '#ffffff',
//           fontSize: '24px',
//           fontWeight: '300',
//           marginBottom: '30px',
//           letterSpacing: '1px'
//         }}>Alerts & Notices</h2>
        
//         <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '20px' }}>
//           {alerts.map((alert, index) => (
//             <div
//               key={index}
//               style={{
//                 background: 'rgba(255,255,255,0.03)',
//                 padding: '25px',
//                 borderRadius: '12px',
//                 border: '1px solid rgba(255,255,255,0.06)',
//                 display: 'flex',
//                 alignItems: 'center',
//                 gap: '15px'
//               }}
//             > 
//               <span style={{ fontSize: '32px' }}>{alert.icon}</span>
//               <p style={{
//                 color: '#e0e0e0',
//                 fontSize: '16px',
//                 fontWeight: '300',
//                 margin: 0
//               }}>
//                 {alert.message}
//               </p>
//             </div>
//           ))}
          
//           <div style={{
//             marginTop: 'auto',
//             padding: '20px',
//             background: 'rgba(255,255,255,0.02)',
//             borderRadius: '12px',
//             textAlign: 'center',
//             border: '1px solid rgba(255,255,255,0.04)'
//           }}>
//             <p style={{
//               color: '#666',
//               fontSize: '14px',
//               margin: 0
//             }}>
//               Last Updated: {new Date().toLocaleTimeString()}
//             </p>
//           </div>
//         </div>
//       </div>
//     </div>
//   );
// };























// import React, { useState, useEffect, useRef } from 'react';

// // =================================== Final With Images ====================================================================
// export const FoodCards = ({ foodRecommendations }) => {
//   const CARD_WIDTH = 350; 
//   const CARD_HEIGHT = 500; 
//   const CARD_SPACING = 200; 
//   const VISIBLE_CARDS = 3; 

//   const [trackOffset, setTrackOffset] = useState(0);
//   const [isShuffling, setIsShuffling] = useState(false);
//   const animationRef = useRef(null);
//   const lastShownIndexRef = useRef(-1);

//   const totalCards = foodRecommendations?.length || 0;

//   const startShuffle = () => {
//     if (totalCards <= 1) return;

//     setIsShuffling(true);

//     // Pick random target different from last
//     let targetIndex;
//     do {
//       targetIndex = Math.floor(Math.random() * totalCards);
//     } while (targetIndex === lastShownIndexRef.current && totalCards > 1);
    
//     lastShownIndexRef.current = targetIndex;

//     // Calculate distance
//     const currentPosition = trackOffset;
//     const currentIndex = Math.floor(currentPosition) % totalCards;
    
//     let distance = (targetIndex - currentIndex + totalCards) % totalCards;
//     if (distance === 0) distance = totalCards;
    
//     const extraRotations = 2 + Math.floor(Math.random() * 2);
//     const totalDistance = distance + (extraRotations * totalCards);
//     const targetPosition = currentPosition + totalDistance;

//     let velocity = 1.5;
//     let offset = currentPosition;
//     const startTime = Date.now();
//     const duration = 2800;

//     const animate = () => {
//       const elapsed = Date.now() - startTime;
//       const progress = Math.min(elapsed / duration, 1);

//       const easeOut = 1 - Math.pow(1 - progress, 5);
//       const newOffset = currentPosition + (totalDistance * easeOut);

//       setTrackOffset(newOffset);

//       if (progress < 1) {
//         animationRef.current = requestAnimationFrame(animate);
//       } else {
//         const finalPosition = Math.round(targetPosition);
//         setTrackOffset(finalPosition);
//         setIsShuffling(false);
        
//         setTimeout(startShuffle, 1500);
//       }
//     };

//     animationRef.current = requestAnimationFrame(animate);
//   };

//   useEffect(() => {
//     if (!foodRecommendations || foodRecommendations.length === 0) return;

//     const timer = setTimeout(startShuffle, 1000);

//     return () => {
//       clearTimeout(timer);
//       if (animationRef.current) {
//         cancelAnimationFrame(animationRef.current);
//       }
//     };
//   }, [foodRecommendations]);

//   if (!foodRecommendations || foodRecommendations.length === 0) {
//     return (
//       <div
//         style={{
//           width: "50%",
//           height: "100vh",
//           background: "#0a0a0a",
//           display: "flex",
//           alignItems: "center",
//           justifyContent: "center",
//         }}
//       >
//         <p style={{ color: "#666", fontSize: "20px" }}>
//           No recommendations available
//         </p>
//       </div>
//     );
//   }

//   const visibleCards = [];
//   for (let offset = -VISIBLE_CARDS; offset <= VISIBLE_CARDS; offset++) {
//     const baseIndex = Math.floor(trackOffset) + offset;
//     const wrappedIndex = ((baseIndex % totalCards) + totalCards) % totalCards;
//     visibleCards.push({ 
//       index: wrappedIndex,
//       position: baseIndex
//     });
//   }

//   let centerCardIndex = Math.round(trackOffset) % totalCards;
//   if (centerCardIndex < 0) centerCardIndex += totalCards;

//   return (
//     <div
//       style={{
//         width: "50%",
//         height: "100vh",
//         background: "#0a0a0a",
//         display: "flex",
//         alignItems: "center",
//         justifyContent: "center",
//         position: "relative",
//         overflow: "hidden",
//       }}
//     >
//       <div
//         style={{
//           position: "relative",
//           width: "100%",
//           height: "100%",
//           display: "flex",
//           alignItems: "center",
//           justifyContent: "center",
//           perspective: "1500px",
//           perspectiveOrigin: "center center",
//         }}
//       >
//         {visibleCards.map(({ index, position }) => {
//           const cardOffset = position - trackOffset;
//           const absOffset = Math.abs(cardOffset);
//           const isCenter = absOffset < 0.5;

//           const translateX = cardOffset * CARD_SPACING;
//           const translateZ = isCenter ? 0 : -absOffset * 150;
//           const translateY = isCenter ? -6 : 0;
//           const rotateY = cardOffset * -22;
//           const rotateX = isCenter ? 0 : 8;
//           const brightness = isCenter ? 1 : Math.max(0.35, 1 - absOffset * 0.22);
//           const blur = isCenter ? 0 : Math.min(2.5, absOffset * 0.8);
//           const scale = isCenter && !isShuffling ? 1.005 : 1;

//           const food = foodRecommendations[index];

//           return (
//             <div
//               key={`${index}-${position}`}
//               style={{
//                 position: "absolute",
//                 width: `${CARD_WIDTH}px`,
//                 height: `${CARD_HEIGHT}px`,
//                 transformStyle: "preserve-3d",
//                 transform: `translate3d(${translateX}px, ${translateY}px, ${translateZ}px) rotateY(${rotateY}deg) rotateX(${rotateX}deg) scale(${scale})`,
//                 opacity: 1,
//                 filter: `brightness(${brightness}) blur(${blur}px)`,
//                 zIndex: isCenter ? 100 : Math.round(50 - absOffset * 10),
//                 transition: isShuffling ? "none" : "transform 0.3s ease-out",
//               }}
//             >
//               <div
//                 style={{
//                   width: "100%",
//                   height: "100%",
//                   background: `url(${food.image}) center/cover no-repeat`,
//                   border: "2px solid rgba(0, 200, 120, 0.35)",
//                   boxShadow: isCenter
//                     ? `0 0 18px rgba(0, 255, 170, 0.45), 0 0 40px rgba(0, 255, 170, 0.25), 0 35px 90px rgba(0,0,0,0.6)`
//                     : `0 0 6px rgba(0, 255, 170, 0.18), 0 15px 40px rgba(0,0,0,0.45)`,
//                   borderRadius: "20px",
//                   boxSizing: "border-box",
//                   transition: "box-shadow 0.6s ease",
//                 }}
//               />
//             </div>
//           );
//         })}
//       </div>

//       <div
//         style={{
//           position: "absolute",
//           bottom: "40px",
//           display: "flex",
//           gap: "10px",
//           zIndex: 200,
//         }}
//       >
//         {foodRecommendations.map((_, idx) => {
//           const normalizedCenter = ((Math.round(trackOffset) % totalCards) + totalCards) % totalCards;
//           return (
//             <div
//               key={idx}
//               style={{
//                 width: "10px",
//                 height: "10px",
//                 borderRadius: "50%",
//                 background: idx === normalizedCenter ? "#fff" : "#333",
//                 transition: "background 0.4s ease",
//                 boxShadow:
//                   idx === normalizedCenter
//                     ? "0 0 12px rgba(255,255,255,0.6)"
//                     : "none",
//               }}
//             />
//           );
//         })}
//       </div>
//     </div>
//   );
// };

// // Alerts Panel Component
// const AlertsPanel = ({ weatherData }) => {
//   const [alerts, setAlerts] = useState([]);

//   useEffect(() => {
//     const newAlerts = [];
    
//     // Temperature alerts
//     if (weatherData.temperature > 40) {
//       newAlerts.push({
//         type: 'danger',
//         message: 'Extreme heat warning',
//         icon: '🔥'
//       });
//     } else if (weatherData.heatIndex > 35) {
//       newAlerts.push({
//         type: 'warning',
//         message: 'High heat index alert',
//         icon: '⚠️'
//       });
//     }
    
//     if (weatherData.temperature < 5) {
//       newAlerts.push({
//         type: 'warning',
//         message: 'Cold weather warning',
//         icon: '❄️'
//       });
//     }
    
//     // Humidity alerts
//     if (weatherData.humidity > 85) {
//       newAlerts.push({
//         type: 'info',
//         message: 'Very high humidity levels',
//         icon: '💧'
//       });
//     }
    
//     // Wind alerts
//     if (weatherData.windSpeed > 40) {
//       newAlerts.push({
//         type: 'warning',
//         message: 'Strong wind advisory',
//         icon: '💨'
//       });
//     } else if (weatherData.windSpeed > 25) {
//       newAlerts.push({
//         type: 'info',
//         message: 'Moderate winds expected',
//         icon: '🍃'
//       });
//     }
    
//     // Rain alerts
//     if (weatherData.rainChance > 70) {
//       newAlerts.push({
//         type: 'info',
//         message: 'High chance of rain',
//         icon: '🌧️'
//       });
//     }
    
//     // UV alerts
//     if (weatherData.uvIndex > 8) {
//       newAlerts.push({
//         type: 'warning',
//         message: 'Very high UV index',
//         icon: '☀️'
//       });
//     }
    
//     // All clear
//     if (newAlerts.length === 0) {
//       newAlerts.push({
//         type: 'success',
//         message: 'Weather conditions normal',
//         icon: '✓'
//       });
//     }
    
//     setAlerts(newAlerts);
//   }, [weatherData]);

//   return (
//     <div style={{
//       width: '25%',
//       height: '100vh',
//       padding: '20px',
//       display: 'flex',
//       flexDirection: 'column'
//     }}>
//       <div style={{
//         height: '100%',
//         background: 'rgba(15, 15, 15, 0.6)',
//         backdropFilter: 'blur(10px)',
//         WebkitBackdropFilter: 'blur(10px)',
//         border: '1px solid rgba(255, 255, 255, 0.08)',
//         borderRadius: '24px',
//         padding: '40px 30px',
//         boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.37)',
//         display: 'flex',
//         flexDirection: 'column',
//         overflowY: 'auto'
//       }}>
//       <h2 style={{
//         color: '#ffffff',
//         fontSize: '24px',
//         fontWeight: '300',
//         marginBottom: '30px',
//         letterSpacing: '1px'
//       }}>Alerts & Notices</h2>
      
//       <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '20px' }}>
//         {alerts.map((alert, index) => (
//           <div
//             key={index}
//             style={{
//               background: 'rgba(255,255,255,0.03)',
//               padding: '25px',
//               borderRadius: '12px',
//               border: '1px solid rgba(255,255,255,0.06)',
//               display: 'flex',
//               alignItems: 'center',
//               gap: '15px'
//             }}
//           > 
//             <span style={{ fontSize: '32px' }}>{alert.icon}</span>
//             <p style={{
//               color: '#e0e0e0',
//               fontSize: '16px',
//               fontWeight: '300',
//               margin: 0
//             }}>
//               {alert.message}
//             </p>
//           </div>
//         ))}
        
//         <div style={{
//           marginTop: 'auto',
//           padding: '20px',
//           background: 'rgba(255,255,255,0.02)',
//           borderRadius: '12px',
//           textAlign: 'center',
//           border: '1px solid rgba(255,255,255,0.04)'
//         }}>
//           <p style={{
//             color: '#666',
//             fontSize: '14px',
//             margin: 0
//           }}>
//             Last Updated: {new Date().toLocaleTimeString()}
//           </p>
//         </div>
//       </div>
//       </div>
//     </div>
//   );
// };


// =================================== Final With Emojis ====================================================================



// const FoodCards = ({ foodRecommendations }) => {
//   const CARD_WIDTH = 350; 
//   const CARD_HEIGHT = 500; 
//   const CARD_SPACING = 200; 
//   const VISIBLE_CARDS = 3; 

//   const [trackOffset, setTrackOffset] = useState(0);
//   const [isShuffling, setIsShuffling] = useState(false);
//   const animationRef = useRef(null);
//   const lastShownIndexRef = useRef(-1);

//   const totalCards = foodRecommendations?.length || 0;

//   const startShuffle = () => {
//     if (totalCards <= 1) return;

//     setIsShuffling(true);

//     // Pick random target different from last
//     let targetIndex;
//     do {
//       targetIndex = Math.floor(Math.random() * totalCards);
//     } while (targetIndex === lastShownIndexRef.current && totalCards > 1);
    
//     lastShownIndexRef.current = targetIndex;

//     // Calculate distance
//     const currentPosition = trackOffset;
//     const currentIndex = Math.floor(currentPosition) % totalCards;
    
//     let distance = (targetIndex - currentIndex + totalCards) % totalCards;
//     if (distance === 0) distance = totalCards;
    
//     const extraRotations = 2 + Math.floor(Math.random() * 2);
//     const totalDistance = distance + (extraRotations * totalCards);
//     const targetPosition = currentPosition + totalDistance;

//     let velocity = 1.5; // Start slower
//     let offset = currentPosition;
//     const startTime = Date.now();
//     const duration = 2800; // Total animation duration in ms

//     const animate = () => {
//       const elapsed = Date.now() - startTime;
//       const progress = Math.min(elapsed / duration, 1);

//       // Cubic ease-out: decelerates smoothly
//       const easeOut = 1 - Math.pow(1 - progress, 5);
//       const newOffset = currentPosition + (totalDistance * easeOut);

//       setTrackOffset(newOffset);

//       if (progress < 1) {
//         animationRef.current = requestAnimationFrame(animate);
//       } else {
//         const finalPosition = Math.round(targetPosition);
//         setTrackOffset(finalPosition);
//         setIsShuffling(false);
        
//         // Continue shuffling after pause
//         setTimeout(startShuffle, 1500);
//       }
//     };

//     animationRef.current = requestAnimationFrame(animate);
//   };

//   useEffect(() => {
//     if (!foodRecommendations || foodRecommendations.length === 0) return;

//     const timer = setTimeout(startShuffle, 1000);

//     return () => {
//       clearTimeout(timer);
//       if (animationRef.current) {
//         cancelAnimationFrame(animationRef.current);
//       }
//     };
//   }, [foodRecommendations]);

//   if (!foodRecommendations || foodRecommendations.length === 0) {
//     return (
//       <div
//         style={{
//           width: "50%",
//           height: "100vh",
//           background: "#0a0a0a",
//           display: "flex",
//           alignItems: "center",
//           justifyContent: "center",
//         }}
//       >
//         <p style={{ color: "#666", fontSize: "20px" }}>
//           No recommendations available
//         </p>
//       </div>
//     );
//   }

//   const visibleCards = [];
//   for (let offset = -VISIBLE_CARDS; offset <= VISIBLE_CARDS; offset++) {
//     const baseIndex = Math.floor(trackOffset) + offset;
//     const wrappedIndex = ((baseIndex % totalCards) + totalCards) % totalCards;
//     visibleCards.push({ 
//       index: wrappedIndex,
//       position: baseIndex
//     });
//   }

//   let centerCardIndex = Math.round(trackOffset) % totalCards;
//   if (centerCardIndex < 0) centerCardIndex += totalCards;

//   return (
//     <div
//       style={{
//         width: "50%",
//         height: "100vh",
//         background: "#0a0a0a",
//         display: "flex",
//         alignItems: "center",
//         justifyContent: "center",
//         position: "relative",
//         overflow: "hidden",
//       }}
//     >
//       <div
//         style={{
//           position: "relative",
//           width: "100%",
//           height: "100%",
//           display: "flex",
//           alignItems: "center",
//           justifyContent: "center",
//           perspective: "1500px",
//           perspectiveOrigin: "center center",
//         }}
//       >
//         {visibleCards.map(({ index, position }) => {
//           const cardOffset = position - trackOffset;
//           const absOffset = Math.abs(cardOffset);
//           const isCenter = absOffset < 0.5;

//           const translateX = cardOffset * CARD_SPACING;
//           const translateZ = isCenter ? 0 : -absOffset * 150;
//           const translateY = isCenter ? -6 : 0;
//           const rotateY = cardOffset * -22;
//           const rotateX = isCenter ? 0 : 8;
//           const brightness = isCenter ? 1 : Math.max(0.35, 1 - absOffset * 0.22);
//           const blur = isCenter ? 0 : Math.min(2.5, absOffset * 0.8);
//           const scale = isCenter && !isShuffling ? 1.005 : 1;

//           const food = foodRecommendations[index];

//           return (
//             <div
//               key={`${index}-${position}`}
//               style={{
//                 position: "absolute",
//                 width: `${CARD_WIDTH}px`,
//                 height: `${CARD_HEIGHT}px`,
//                 transformStyle: "preserve-3d",
//                 transform: `translate3d(${translateX}px, ${translateY}px, ${translateZ}px) rotateY(${rotateY}deg) rotateX(${rotateX}deg) scale(${scale})`,
//                 opacity: 1,
//                 filter: `brightness(${brightness}) blur(${blur}px)`,
//                 zIndex: isCenter ? 100 : Math.round(50 - absOffset * 10),
//                 transition: isShuffling ? "none" : "transform 0.3s ease-out",
//               }}
//             >
//               <div
//                 style={{
//                   width: "100%",
//                   height: "100%",
//                   background: "linear-gradient(135deg, #1e1e1e, #141414)",
//                   border: "2px solid rgba(0, 200, 120, 0.35)",
//                   boxShadow: isCenter
//                     ? `0 0 18px rgba(0, 255, 170, 0.45), 0 0 40px rgba(0, 255, 170, 0.25), 0 35px 90px rgba(0,0,0,0.6)`
//                     : `0 0 6px rgba(0, 255, 170, 0.18), 0 15px 40px rgba(0,0,0,0.45)`,
//                   borderRadius: "20px",
//                   padding: "45px 30px",
//                   textAlign: "center",
//                   display: "flex",
//                   flexDirection: "column",
//                   justifyContent: "center",
//                   alignItems: "center",
//                   boxSizing: "border-box",
//                   transition: "box-shadow 0.6s ease",
//                 }}
//               >
//                 <div
//                   style={{
//                     fontSize: "100px",
//                     marginBottom: "20px",
//                     filter: "drop-shadow(0 8px 16px rgba(0,0,0,0.3))",
//                   }}
//                 >
//                   <img src={food.image} />
//                 </div>

//                 <h2
//                   style={{
//                     color: "#ffffff",
//                     fontSize: "28px",
//                     fontWeight: "400",
//                     marginBottom: "15px",
//                     letterSpacing: "0.5px",
//                     margin: "0 0 15px 0",
//                   }}
//                 >
//                   {food.name}
//                 </h2>

//                 <p
//                   style={{
//                     color: food.color,
//                     fontSize: "16px",
//                     fontWeight: "300",
//                     lineHeight: "1.5",
//                     margin: 0,
//                   }}
//                 >
//                   {food.reason}
//                 </p>
//               </div>
//             </div>
//           );
//         })}
//       </div>

//       <div
//         style={{
//           position: "absolute",
//           bottom: "40px",
//           display: "flex",
//           gap: "10px",
//           zIndex: 200,
//         }}
//       >
//         {foodRecommendations.map((_, idx) => {
//           const normalizedCenter = ((Math.round(trackOffset) % totalCards) + totalCards) % totalCards;
//           return (
//             <div
//               key={idx}
//               style={{
//                 width: "10px",
//                 height: "10px",
//                 borderRadius: "50%",
//                 background: idx === normalizedCenter ? "#fff" : "#333",
//                 transition: "background 0.4s ease",
//                 boxShadow:
//                   idx === normalizedCenter
//                     ? "0 0 12px rgba(255,255,255,0.6)"
//                     : "none",
//               }}
//             />
//           );
//         })}
//       </div>
//     </div>
//   );
// };